import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '@/lib/query-keys';

interface RemoveMembershipPlanLocationInput {
  planId: string;
  locationId: string;
}

interface UseRemoveMembershipPlanLocationOptions {
  onSuccess?: () => void;
}

/**
 * Take this branch off the record, leaving it in place everywhere else.
 *
 * The other half of the delete prompt. The server refuses (409) when this is
 * the LAST branch — zero rows reads as "every branch", so a withdrawal has to
 * be a deactivation instead. That message is surfaced verbatim, because it
 * tells the operator exactly what to do next.
 */
export const useRemoveMembershipPlanLocation = (
  options?: UseRemoveMembershipPlanLocationOptions
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ planId, locationId }: RemoveMembershipPlanLocationInput) =>
      apiClient.delete(`membership-plans/${planId}/locations/${locationId}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.membershipPlans.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.membershipPlans.detail(variables.planId),
      });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not remove it from this location');
    },
  });

  return {
    removeMembershipPlanLocation: mutation.mutate,
    removeMembershipPlanLocationAsync: mutation.mutateAsync,
    isRemoving: mutation.isPending,
  };
};
